<template>
  <div class="min-h-screen bg-slate-50">
    <!-- 헤더 -->
    <header class="bg-white border-b border-slate-100 sticky top-0 z-10">
      <div class="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
        <router-link to="/partners/intro" class="flex items-center gap-2 text-slate-600 hover:text-blue-600">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
          <span class="text-[14px] font-medium">뒤로</span>
        </router-link>
        <div class="text-center">
          <h1 class="font-bold text-slate-800">파트너 등록</h1>
        </div>
        <div class="w-16"></div>
      </div>
    </header>

    <!-- 진행 단계 -->
    <div class="max-w-2xl mx-auto px-4 py-6">
      <div class="flex items-center justify-between mb-8">
        <template v-for="(step, index) in steps" :key="step.id">
          <div class="flex items-center">
            <div
              :class="[
                'w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all',
                currentStep >= step.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-200 text-slate-400'
              ]"
            >
              <svg v-if="currentStep > step.id" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
              </svg>
              <span v-else>{{ step.id }}</span>
            </div>
            <span
              :class="[
                'ml-2 text-[13px] font-bold hidden sm:block',
                currentStep >= step.id ? 'text-blue-600' : 'text-slate-400'
              ]"
            >
              {{ step.title }}
            </span>
          </div>
          <div
            v-if="index < steps.length - 1"
            :class="[
              'flex-1 h-0.5 mx-3',
              currentStep > step.id ? 'bg-blue-600' : 'bg-slate-200'
            ]"
          ></div>
        </template>
      </div>

      <!-- 컨텐츠 카드 -->
      <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8">
        <!-- Step 1: 기본 정보 -->
        <div v-if="currentStep === 1">
          <h2 class="text-xl font-black text-slate-900 mb-2">기본 정보를 입력해주세요</h2>
          <p class="text-[14px] text-slate-500 mb-6">정확한 정보를 입력해야 심사가 원활히 진행됩니다.</p>

          <div class="space-y-4">
            <div>
              <label class="form-label">이름 (실명) <span class="required">*</span></label>
              <input
                v-model="form.name"
                type="text"
                placeholder="홍길동"
                class="form-input"
                maxlength="20"
              />
            </div>

            <div>
              <label class="form-label">연락처 <span class="required">*</span></label>
              <input
                v-model="form.phone"
                type="tel"
                placeholder="010-1234-5678"
                class="form-input"
                @input="formatPhone"
              />
            </div>

            <div>
              <label class="form-label">소속 회사 <span class="required">*</span></label>
              <input
                v-model="form.company"
                type="text"
                placeholder="부동산 회사명"
                class="form-input"
                maxlength="50"
              />
            </div>

            <div>
              <label class="form-label">직함/직급 <span class="required">*</span></label>
              <input
                v-model="form.position"
                type="text"
                placeholder="팀장, 과장, 분양대표 등"
                class="form-input"
                maxlength="30"
              />
            </div>

            <div>
              <label class="form-label">경력 사항 <span class="optional">(선택)</span></label>
              <textarea
                v-model="form.experience"
                placeholder="분양 관련 경력을 간단히 작성해주세요"
                class="form-textarea"
                rows="3"
                maxlength="200"
              ></textarea>
              <p class="char-count">{{ form.experience.length }}/200</p>
            </div>
          </div>
        </div>

        <!-- Step 2: 현장 선택 -->
        <div v-if="currentStep === 2">
          <h2 class="text-xl font-black text-slate-900 mb-2">활동할 현장을 선택해주세요</h2>
          <p class="text-[14px] text-slate-500 mb-6">현재 영업 중인 현장을 선택하세요. 추후 변경할 수 있습니다.</p>

          <!-- 직접 입력 / 목록 선택 토글 -->
          <div class="flex bg-slate-100 rounded-xl p-1 mb-5">
            <button
              @click="propertyInputMode = 'select'"
              :class="[
                'flex-1 py-2.5 text-[14px] font-bold rounded-lg transition-all',
                propertyInputMode === 'select'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500'
              ]"
            >
              목록에서 선택
            </button>
            <button
              @click="propertyInputMode = 'manual'"
              :class="[
                'flex-1 py-2.5 text-[14px] font-bold rounded-lg transition-all',
                propertyInputMode === 'manual'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500'
              ]"
            >
              직접 입력
            </button>
          </div>

          <!-- 목록에서 선택 -->
          <template v-if="propertyInputMode === 'select'">
            <!-- 검색 -->
            <div class="relative mb-4">
              <svg class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input
                v-model="propertySearch"
                type="text"
                placeholder="현장명으로 검색"
                class="form-input pl-12"
              />
            </div>

            <!-- 로딩 -->
            <div v-if="loadingProperties" class="py-12 text-center">
              <div class="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3"></div>
              <p class="text-slate-500 text-[14px]">현장 목록을 불러오는 중...</p>
            </div>

            <!-- 현장 목록 -->
            <div v-else class="space-y-2 max-h-72 overflow-y-auto">
              <div
                v-for="property in filteredProperties"
                :key="property.id"
                @click="selectProperty(property)"
                :class="[
                  'p-4 rounded-xl border-2 cursor-pointer transition-all',
                  form.propertyId === property.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-blue-300'
                ]"
              >
                <div class="flex items-start gap-3">
                  <div
                    :class="[
                      'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
                      form.propertyId === property.id
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-slate-300'
                    ]"
                  >
                    <svg v-if="form.propertyId === property.id" class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="font-bold text-slate-800">{{ property.name }}</h3>
                    <p class="text-[13px] text-slate-500 truncate">{{ property.address }}</p>
                    <div class="flex items-center gap-2 mt-1">
                      <span :class="['text-[11px] px-2 py-0.5 rounded-full font-medium', getStatusClass(property.status)]">
                        {{ getStatusLabel(property.status) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="filteredProperties.length === 0 && !loadingProperties" class="py-8 text-center">
                <svg class="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <p class="text-slate-500 text-[14px]">검색 결과가 없습니다</p>
                <button
                  @click="propertyInputMode = 'manual'"
                  class="mt-3 text-blue-600 font-bold text-[14px]"
                >
                  직접 입력하기
                </button>
              </div>
            </div>
          </template>

          <!-- 직접 입력 -->
          <template v-else>
            <div class="space-y-4">
              <div class="p-4 bg-blue-50 rounded-xl border border-blue-100 mb-4">
                <div class="flex items-start gap-3">
                  <svg class="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <div>
                    <p class="text-[13px] text-blue-800 font-medium">목록에 없는 현장이신가요?</p>
                    <p class="text-[12px] text-blue-700 mt-1">
                      직접 현장 정보를 입력하시면 관리자 확인 후 등록됩니다.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label class="form-label">현장명 <span class="required">*</span></label>
                <input
                  v-model="form.manualPropertyName"
                  type="text"
                  placeholder="예: 래미안 원베일리"
                  class="form-input"
                  maxlength="50"
                />
              </div>

              <div>
                <label class="form-label">현장 주소 <span class="required">*</span></label>
                <input
                  v-model="form.manualPropertyAddress"
                  type="text"
                  placeholder="예: 서울시 서초구 반포동"
                  class="form-input"
                  maxlength="100"
                />
              </div>

              <div>
                <label class="form-label">시행/시공사 <span class="optional">(선택)</span></label>
                <input
                  v-model="form.manualPropertyDeveloper"
                  type="text"
                  placeholder="예: 삼성물산"
                  class="form-input"
                  maxlength="50"
                />
              </div>
            </div>
          </template>
        </div>

        <!-- Step 3: 명함 업로드 -->
        <div v-if="currentStep === 3">
          <h2 class="text-xl font-black text-slate-900 mb-2">명함을 업로드해주세요</h2>
          <p class="text-[14px] text-slate-500 mb-6">명함 사진으로 본인 확인을 진행합니다.</p>

          <div
            @click="triggerFileInput"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            :class="[
              'border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all',
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : businessCardPreview
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
            ]"
          >
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              class="hidden"
              @change="handleFileSelect"
            />

            <div v-if="businessCardPreview">
              <img :src="businessCardPreview" alt="명함 미리보기" class="max-h-48 mx-auto rounded-xl mb-4 shadow-sm" />
              <p class="text-[13px] text-blue-600 font-medium">클릭하여 다른 이미지 선택</p>
            </div>
            <div v-else>
              <div class="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>
              <p class="text-slate-700 font-bold mb-2">명함 사진을 업로드하세요</p>
              <p class="text-[13px] text-slate-400">클릭하거나 파일을 드래그하세요</p>
              <p class="text-[12px] text-slate-400 mt-2">JPG, PNG, WEBP (최대 10MB)</p>
            </div>
          </div>

          <div class="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
            <div class="flex items-start gap-3">
              <svg class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <div>
                <p class="text-[13px] text-amber-800 font-bold">명함 확인 안내</p>
                <p class="text-[12px] text-amber-700 mt-1">
                  이름, 소속, 연락처가 명확히 보이는 명함 사진을 업로드해주세요.
                  불명확한 이미지는 승인이 거절될 수 있습니다.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4: 영업 정보 -->
        <div v-if="currentStep === 4">
          <h2 class="text-xl font-black text-slate-900 mb-2">영업 정보를 설정해주세요</h2>
          <p class="text-[14px] text-slate-500 mb-6">고객에게 보여지는 프로필 정보입니다.</p>

          <div class="space-y-4">
            <div>
              <label class="form-label">영업명 <span class="required">*</span></label>
              <input
                v-model="form.displayName"
                type="text"
                placeholder="고객에게 보여질 이름 (예: 김분양)"
                class="form-input"
                maxlength="10"
              />
              <p class="char-count">{{ form.displayName.length }}/10</p>
              <p class="text-[12px] text-slate-400 mt-1">고객에게 노출되는 이름입니다. 실명 또는 영업명을 입력하세요.</p>
            </div>

            <div>
              <label class="form-label">한 줄 소개 <span class="required">*</span></label>
              <textarea
                v-model="form.introduction"
                placeholder="고객에게 어필할 한 줄 소개를 작성해주세요"
                class="form-textarea"
                rows="2"
                maxlength="30"
              ></textarea>
              <p class="char-count">{{ form.introduction.length }}/30</p>
              <p class="text-[12px] text-slate-400 mt-1">예: "10년 경력의 분양 전문가입니다", "친절한 상담을 약속드립니다"</p>
            </div>

            <!-- 미리보기 -->
            <div class="mt-6">
              <p class="text-[13px] font-bold text-slate-700 mb-3">고객 화면 미리보기</p>
              <div class="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div class="flex items-center gap-3">
                  <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span class="text-blue-600 font-black text-lg">{{ form.displayName?.[0] || '?' }}</span>
                  </div>
                  <div>
                    <h4 class="font-bold text-slate-800">{{ form.displayName || '영업명' }}</h4>
                    <p class="text-[13px] text-slate-500">{{ form.introduction || '한 줄 소개' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 에러 메시지 -->
        <div v-if="errorMessage" class="mt-4 p-3 bg-red-50 text-red-600 text-[14px] rounded-xl font-medium">
          {{ errorMessage }}
        </div>

        <!-- 버튼 -->
        <div class="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
          <button
            v-if="currentStep > 1"
            @click="prevStep"
            class="px-6 py-3 text-slate-600 hover:bg-slate-100 rounded-xl transition font-medium"
          >
            이전
          </button>
          <div v-else></div>

          <button
            v-if="currentStep < 4"
            @click="nextStep"
            :disabled="!canProceed"
            :class="[
              'px-8 py-3 rounded-xl font-bold transition',
              canProceed
                ? 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            ]"
          >
            다음
          </button>
          <button
            v-else
            @click="submitApplication"
            :disabled="!canSubmit || submitting"
            :class="[
              'px-8 py-3 rounded-xl font-bold transition',
              canSubmit && !submitting
                ? 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            ]"
          >
            {{ submitting ? '신청 중...' : '등록 신청하기' }}
          </button>
        </div>
      </div>

      <!-- 하단 안내 -->
      <div class="mt-6 text-center">
        <p class="text-[13px] text-slate-500">
          이미 파트너 계정이 있으신가요?
          <router-link to="/partners/login" class="text-blue-600 font-bold hover:underline">
            로그인하기
          </router-link>
        </p>
      </div>
    </div>

    <!-- 성공 모달 -->
    <Teleport to="body">
      <div v-if="showSuccessModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg class="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <h3 class="text-xl font-black text-slate-800 mb-2">등록 신청 완료!</h3>
          <p class="text-slate-500 mb-6">
            관리자 승인 후 파트너스센터를 이용하실 수 있습니다.
            <br/>승인까지 1~2영업일 소요됩니다.
          </p>
          <button
            @click="goToHome"
            class="w-full py-3.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition active:scale-[0.98]"
          >
            확인
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { collection, addDoc, getDocs, query, orderBy, where } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'

export default {
  name: 'PartnersRegisterView',
  setup() {
    const router = useRouter()
    const { user, isLoggedIn } = useAuth()

    const currentStep = ref(1)
    const submitting = ref(false)
    const errorMessage = ref('')
    const showSuccessModal = ref(false)

    const steps = [
      { id: 1, title: '기본 정보' },
      { id: 2, title: '현장 선택' },
      { id: 3, title: '명함 업로드' },
      { id: 4, title: '영업 정보' }
    ]

    // 폼 데이터
    const form = ref({
      name: '',
      phone: '',
      company: '',
      position: '',
      experience: '',
      propertyId: '',
      propertyName: '',
      displayName: '',
      introduction: '',
      // 직접 입력용
      manualPropertyName: '',
      manualPropertyAddress: '',
      manualPropertyDeveloper: ''
    })

    // 현장 관련
    const properties = ref([])
    const loadingProperties = ref(false)
    const propertySearch = ref('')
    const propertyInputMode = ref('select') // 'select' or 'manual'

    // 명함 관련
    const fileInput = ref(null)
    const businessCardFile = ref(null)
    const businessCardPreview = ref(null)
    const isDragging = ref(false)

    const filteredProperties = computed(() => {
      if (!propertySearch.value) return properties.value
      const search = propertySearch.value.toLowerCase()
      return properties.value.filter(p =>
        p.name.toLowerCase().includes(search) ||
        p.address?.toLowerCase().includes(search)
      )
    })

    const canProceed = computed(() => {
      switch (currentStep.value) {
        case 1:
          return form.value.name.trim() &&
                 form.value.phone.trim() &&
                 form.value.company.trim() &&
                 form.value.position.trim()
        case 2:
          if (propertyInputMode.value === 'select') {
            return form.value.propertyId
          } else {
            return form.value.manualPropertyName.trim() &&
                   form.value.manualPropertyAddress.trim()
          }
        case 3:
          return businessCardFile.value
        default:
          return true
      }
    })

    const canSubmit = computed(() => {
      return form.value.displayName.trim().length >= 2 &&
             form.value.introduction.trim().length >= 5
    })

    const formatPhone = (e) => {
      let value = e.target.value.replace(/[^0-9]/g, '')
      if (value.length > 11) value = value.slice(0, 11)

      if (value.length > 7) {
        form.value.phone = `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7)}`
      } else if (value.length > 3) {
        form.value.phone = `${value.slice(0, 3)}-${value.slice(3)}`
      } else {
        form.value.phone = value
      }
    }

    const getStatusLabel = (status) => {
      const labels = {
        'pre-sale': '분양예정',
        'on-sale': '분양중',
        'completed': '분양완료'
      }
      return labels[status] || status
    }

    const getStatusClass = (status) => {
      const classes = {
        'pre-sale': 'bg-amber-100 text-amber-700',
        'on-sale': 'bg-emerald-100 text-emerald-700',
        'completed': 'bg-slate-100 text-slate-600'
      }
      return classes[status] || 'bg-slate-100 text-slate-600'
    }

    const selectProperty = (property) => {
      form.value.propertyId = property.id
      form.value.propertyName = property.name
    }

    const triggerFileInput = () => {
      fileInput.value?.click()
    }

    const handleFileSelect = (e) => {
      const file = e.target.files?.[0]
      if (file) processFile(file)
    }

    const handleDrop = (e) => {
      isDragging.value = false
      const file = e.dataTransfer.files?.[0]
      if (file) processFile(file)
    }

    const processFile = (file) => {
      // 이미지 타입 체크
      if (!file.type.startsWith('image/')) {
        errorMessage.value = '이미지 파일만 업로드 가능합니다.'
        return
      }

      // 용량 체크 (10MB)
      if (file.size > 10 * 1024 * 1024) {
        errorMessage.value = '10MB 이하의 이미지만 업로드 가능합니다.'
        return
      }

      businessCardFile.value = file
      errorMessage.value = ''

      // 미리보기 생성
      const reader = new FileReader()
      reader.onload = (e) => {
        businessCardPreview.value = e.target.result
      }
      reader.readAsDataURL(file)
    }

    const prevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
        errorMessage.value = ''
      }
    }

    const nextStep = () => {
      if (!isLoggedIn.value) {
        errorMessage.value = '로그인이 필요합니다.'
        router.push('/partners/login')
        return
      }

      if (canProceed.value && currentStep.value < 4) {
        currentStep.value++
        errorMessage.value = ''
      }
    }

    const uploadBusinessCard = async () => {
      if (!businessCardFile.value || !user.value) return null

      const fileName = `${Date.now()}_${businessCardFile.value.name}`
      const fileRef = storageRef(storage, `business-cards/${user.value.uid}/${fileName}`)

      await uploadBytes(fileRef, businessCardFile.value)
      return await getDownloadURL(fileRef)
    }

    const submitApplication = async () => {
      if (!isLoggedIn.value || !user.value) {
        errorMessage.value = '로그인이 필요합니다.'
        return
      }

      if (!canSubmit.value) return

      submitting.value = true
      errorMessage.value = ''

      try {
        // 이미 신청한 내역이 있는지 확인 (pending 상태)
        const existingQuery = query(
          collection(db, 'partnerApplications'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'pending')
        )
        const existingDocs = await getDocs(existingQuery)
        if (!existingDocs.empty) {
          errorMessage.value = '이미 대기 중인 신청이 있습니다. 승인 결과를 기다려주세요.'
          submitting.value = false
          return
        }

        // 명함 업로드
        const businessCardUrl = await uploadBusinessCard()
        if (!businessCardUrl) {
          errorMessage.value = '명함 업로드에 실패했습니다.'
          submitting.value = false
          return
        }

        // 현장 정보 결정
        let propertyId = ''
        let propertyName = ''
        let isManualProperty = false

        if (propertyInputMode.value === 'select') {
          propertyId = form.value.propertyId
          propertyName = form.value.propertyName
        } else {
          // 직접 입력인 경우
          propertyName = form.value.manualPropertyName.trim()
          isManualProperty = true
        }

        // 신청서 저장
        const applicationData = {
          partnerId: user.value.uid,
          partnerEmail: user.value.email,

          name: form.value.name.trim(),
          phone: form.value.phone.trim(),
          company: form.value.company.trim(),
          position: form.value.position.trim(),
          experience: form.value.experience.trim(),

          propertyId: propertyId,
          propertyName: propertyName,

          displayName: form.value.displayName.trim(),
          introduction: form.value.introduction.trim(),

          businessCardUrl,

          status: 'pending',
          rejectReason: '',

          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        // 직접 입력인 경우 추가 정보
        if (isManualProperty) {
          applicationData.isManualProperty = true
          applicationData.manualPropertyAddress = form.value.manualPropertyAddress.trim()
          applicationData.manualPropertyDeveloper = form.value.manualPropertyDeveloper.trim()
        }

        await addDoc(collection(db, 'partnerApplications'), applicationData)

        showSuccessModal.value = true
      } catch (error) {
        console.error('신청 실패:', error)
        errorMessage.value = '신청 처리 중 오류가 발생했습니다. 다시 시도해주세요.'
      } finally {
        submitting.value = false
      }
    }

    const goToHome = () => {
      router.push('/')
    }

    const fetchProperties = async () => {
      loadingProperties.value = true
      try {
        const q = query(collection(db, 'properties'), orderBy('createdAt', 'desc'))
        const snapshot = await getDocs(q)
        properties.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('현장 목록 조회 실패:', error)
      } finally {
        loadingProperties.value = false
      }
    }

    onMounted(() => {
      fetchProperties()

      // 로그인 체크
      if (!isLoggedIn.value) {
        errorMessage.value = '파트너 등록을 위해 먼저 로그인해주세요.'
      }
    })

    return {
      currentStep,
      steps,
      form,
      properties,
      loadingProperties,
      propertySearch,
      propertyInputMode,
      filteredProperties,
      fileInput,
      businessCardPreview,
      isDragging,
      submitting,
      errorMessage,
      showSuccessModal,
      canProceed,
      canSubmit,
      formatPhone,
      getStatusLabel,
      getStatusClass,
      selectProperty,
      triggerFileInput,
      handleFileSelect,
      handleDrop,
      prevStep,
      nextStep,
      submitApplication,
      goToHome
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.form-label {
  @apply block text-[13px] font-bold text-slate-700 mb-2;
}

.required {
  @apply text-red-500;
}

.optional {
  @apply text-slate-400 font-normal;
}

.form-input {
  @apply w-full px-4 py-3 rounded-xl border border-slate-200 outline-none transition text-slate-800 text-[15px];
}

.form-input:focus {
  @apply border-blue-500 ring-2 ring-blue-500/20;
}

.form-textarea {
  @apply w-full px-4 py-3 rounded-xl border border-slate-200 outline-none transition text-slate-800 resize-none text-[15px];
}

.form-textarea:focus {
  @apply border-blue-500 ring-2 ring-blue-500/20;
}

.char-count {
  @apply text-[12px] text-slate-400 text-right mt-1;
}
</style>
